import getDevicePropsArr from '@/constants/device-props';
import { FilterType, PageParameterId, FilterOperator } from '@/types/analytics/analytics.types';
import { eventPropNameMapper } from '@/data-explorer/helpers/common.util';
import { v4 as uuid } from 'uuid';
import get from 'lodash/get';
import union from 'lodash/union';
import isObject from 'lodash/isObject';
import isBoolean from 'lodash/isBoolean';
import isString from 'lodash/isString';
import toLower from 'lodash/toLower';
import isNumber from 'lodash/isNumber';
import { i18n_t as $t } from '@/utils/i18n';

const INTEGER = 'integer';
const FLOAT = 'float';
const BOOLEAN = 'boolean';
const STRING = 'string';
const DATE = 'date';
const TIME = 'time';
const LIST = 'list';

const getOperatorMap = () => {
    return {
        [FilterOperator.Equal]: { label: '=', operator: '==', name: $t('eventPropertyFilter.is equal to') },
        [FilterOperator.NotEqual]: { label: '!=', operator: '!=', name: $t("eventPropertyFilter.isn't equal to") },
        [FilterOperator.Contains]: { label: '~', operator: 'contains', name: $t('eventPropertyFilter.contains') },
        [FilterOperator.NotContains]: {
            label: '!~',
            operator: '!contains',
            name: $t("eventPropertyFilter.doesn't contain"),
        },
        [FilterOperator.GreaterOrEqual]: {
            label: '>=',
            operator: '>=',
            name: $t('eventPropertyFilter.is greater than or equal to'),
        },
        [FilterOperator.LessOrEqual]: {
            label: '<=',
            operator: '<=',
            name: $t('eventPropertyFilter.is less than or equal to'),
        },
        [FilterOperator.Empty]: { label: '()', operator: 'empty', name: $t('eventPropertyFilter.is empty') },
        [FilterOperator.NotEmpty]: { label: '(x)', operator: '!empty', name: $t("eventPropertyFilter.isn't empty") },
        [FilterOperator.True]: {
            label: 'is true',
            operator: '==',
            value: true,
            name: $t('eventPropertyFilter.is true'),
        },
        [FilterOperator.False]: {
            label: 'is false',
            operator: '==',
            value: false,
            name: $t('eventPropertyFilter.is false'),
        },
    };
};

export function convertFilterOperatorToOperator (filterOperator) {
    const operatorMap = getOperatorMap();
    const operatorSettings = operatorMap[filterOperator];
    if (!operatorSettings) return undefined;

    if (filterOperator === FilterOperator.True || filterOperator === FilterOperator.False) {
        return {
            operator: operatorSettings.operator,
            value: operatorSettings.value,
        };
    }

    return { operator: operatorSettings.operator };
}

export function convertOperatorToFilterOperator (operator, schema, value) {
    const operatorMap = getOperatorMap();
    const filterOperators = Object.keys(operatorMap);
    for (const key of filterOperators) {
        if (operatorMap[key].operator === operator) {
            if (schema === BOOLEAN && value === true) {
                return FilterOperator.True;
            }
            if (schema === BOOLEAN && value === false) {
                return FilterOperator.False;
            }

            return key;
        }
    }

    return undefined;
}

export function getOperatorsOptions (schema) {
    const operatorMap = getOperatorMap();
    switch (schema) {
        case BOOLEAN:
            return [operatorMap[FilterOperator.True], operatorMap[FilterOperator.False]];
        case STRING: {
            return [
                operatorMap[FilterOperator.Equal],
                operatorMap[FilterOperator.NotEqual],
                operatorMap[FilterOperator.Contains],
                operatorMap[FilterOperator.NotContains],
                operatorMap[FilterOperator.Empty],
                operatorMap[FilterOperator.NotEmpty],
            ];
        }
        case INTEGER:
        case FLOAT:
        case 'number':
            return [
                operatorMap[FilterOperator.Equal],
                operatorMap[FilterOperator.NotEqual],
                operatorMap[FilterOperator.GreaterOrEqual],
                operatorMap[FilterOperator.LessOrEqual],
                operatorMap[FilterOperator.Empty],
                operatorMap[FilterOperator.NotEmpty],
            ];
        case LIST:
            return [
                operatorMap[FilterOperator.Contains],
                operatorMap[FilterOperator.NotContains],
                operatorMap[FilterOperator.Empty],
                operatorMap[FilterOperator.NotEmpty],
            ];
        default: {
            return [];
        }
    }
}

export function getOperatorNameByLabel (label) {
    const operatorMap = getOperatorMap();

    return get(operatorMap[label], 'name', '');
}

export function operatorNeedsValue (operator) {
    const labels = [
        FilterOperator.Equal,
        FilterOperator.NotEqual,
        FilterOperator.Contains,
        FilterOperator.NotContains,
        FilterOperator.GreaterOrEqual,
        FilterOperator.LessOrEqual,
    ];

    return labels.includes(operator);
}

export function operatorSupportsMultipleValues (operator) {
    const labels = [FilterOperator.GreaterOrEqual, FilterOperator.LessOrEqual];

    return !labels.includes(operator);
}

export function isFilterValid ({ field, operator, comparisonValues }) {
    return !!(field && operator && (comparisonValues?.length || !operatorNeedsValue(operator)));
}

/**
 * Gets the union event property names of all given targets
 *
 * @param targets array of full target objects with the following partial structure
 * {
 *  kind: String,
 *  appId?: Number
 *  platform? String
 *  eventPropertyNameList? Array<String>
 *  eventPropertyConfigurations? Array<Object>
 * }
 */
export function eventPropNames (targets) {
    return union(...targets.map(eventPropNameMapper));
}

/**
 * @param {FnProps} params Config object forwarded to `getDevicePropsArr`
 * @returns Array of device props with standardized key names
 */
export function getDeviceProps (params) {
    return getDevicePropsArr(params).map((deviceProp) => {
        return {
            name: deviceProp.DisplayName,
            filterType: FilterType.Device,
            eventPropertyName: deviceProp.DisplayName,
            eventPropertyField: deviceProp.field,
            eventPropertyKind: deviceProp.kind,
            eventPropertySchema: deviceProp.Type,
            eventPropertyElementType: deviceProp.ElementType,
            automaticEventProperty: deviceProp.isAutomatic,
            eventPropertyFieldKey: `__pendo__device_${deviceProp.field}`,
        };
    });
}

export function getCustomEventProperties (eventProperties = []) {
    return eventProperties
        .map((ep) => {
            return {
                name: ep.DisplayName,
                filterType: FilterType.Custom,
                eventPropertyName: ep.DisplayName,
                eventPropertySchema: ep.Type,
                eventPropertyElementType: ep.ElementType,
                eventPropertyFieldKey: ep.field,
                eventPropertyField: ep.field,
                eventPropertyKind: ep.kind,
                uniqueValues: ep.uniqueValues,
            };
        })
        .filter((ep) => ep && get(ep, 'eventPropertySchema') !== 'time');
}

export function getHistoricalMetadata (historical = []) {
    return historical.map((metadata) => {
        return {
            name: metadata.DisplayName,
            filterType: FilterType.HistoricalMetadata,
            eventPropertyName: metadata.DisplayName,
            eventPropertyField: metadata.field,
            eventPropertySchema: metadata.Type,
            eventPropertyElementType: metadata.ElementType,
            eventPropertyFieldKey: metadata.field,
            eventPropertyKind: metadata.kind,
            eventPropertyGroup: metadata.group,
            uniqueValues: metadata.uniqueValues,
        };
    });
}

export function getAccountMetadata (account = []) {
    return account.map((metadata) => {
        return {
            name: metadata.DisplayName,
            filterType: FilterType.Account,
            eventPropertyName: metadata.DisplayName,
            eventPropertyField: metadata.field,
            eventPropertySchema: metadata.Type,
            eventPropertyElementType: metadata.ElementType ? metadata.ElementType : '',
            eventPropertyFieldKey: `${metadata.kind}.${metadata.group}.${metadata.field}`,
            eventPropertyKind: metadata.kind,
            eventPropertyGroup: metadata.group,
            uniqueValues: metadata.uniqueValues,
        };
    });
}

export function getVisitorMetadata (visitor = []) {
    return visitor.map((metadata) => {
        return {
            name: metadata.DisplayName,
            filterType: FilterType.Visitor,
            eventPropertyName: metadata.DisplayName,
            eventPropertyField: metadata.field,
            eventPropertySchema: metadata.Type,
            eventPropertyElementType: metadata.ElementType ? metadata.ElementType : '',
            eventPropertyFieldKey: `${metadata.kind}.${metadata.group}.${metadata.field}`,
            eventPropertyKind: metadata.kind,
            eventPropertyGroup: metadata.group,
            uniqueValues: metadata.uniqueValues,
        };
    });
}

export function getParentAccountMetadata (parentAccount = []) {
    return parentAccount.map((metadata) => {
        return {
            name: metadata.DisplayName,
            filterType: FilterType.ParentAccount,
            eventPropertyName: metadata.DisplayName,
            eventPropertyField: metadata.field,
            eventPropertySchema: metadata.Type,
            eventPropertyElementType: metadata.ElementType ? metadata.ElementType : '',
            eventPropertyFieldKey: `${metadata.kind}.${metadata.group}.${metadata.field}`,
            eventPropertyKind: metadata.kind,
            eventPropertyGroup: metadata.group,
            uniqueValues: metadata.uniqueValues,
        };
    });
}

export function getSessionProperties (sessionProperties = []) {
    return sessionProperties.map((metadata) => {
        return {
            name: metadata.DisplayName,
            filterType: FilterType.Utm,
            eventPropertyName: metadata.DisplayName,
            eventPropertyField: metadata.field,
            eventPropertySchema: metadata.Type,
            eventPropertyElementType: metadata.ElementType,
            eventPropertyFieldKey: `${metadata.kind}.${metadata.group}.${metadata.field}`,
            eventPropertyKind: metadata.kind,
            eventPropertyGroup: metadata.group,
            uniqueValues: metadata.uniqueValues,
        };
    });
}

export function getLocationProps () {
    return [
        {
            name: $t('eventPropertyFilter.Country'),
            filterType: FilterType.Location,
            eventPropertyName: 'Country',
            eventPropertySchema: STRING,
            eventPropertyElementType: STRING,
            eventPropertyFieldKey: 'country',
            eventPropertyField: 'country',
            eventPropertyKind: '',
            uniqueValues: [],
        },
        {
            name: $t('eventPropertyFilter.State/Region'),
            filterType: FilterType.Location,
            eventPropertyName: 'State/Region',
            eventPropertySchema: STRING,
            eventPropertyElementType: STRING,
            eventPropertyFieldKey: 'country_region',
            eventPropertyField: 'region',
            eventPropertyKind: '',
            uniqueValues: [],
        },
    ];
}

export function getTopLevelProps () {
    return [
        {
            name: $t('eventPropertyFilter.Server name'),
            filterType: FilterType.EventTopLevel,
            eventPropertyName: 'Server name',
            eventPropertySchema: STRING,
            eventPropertyElementType: STRING,
            eventPropertyFieldKey: 'server',
            eventPropertyField: 'server',
            eventPropertyKind: '',
            uniqueValues: [],
        },
    ];
}

export function getPageParameterProps () {
    return [
        {
            name: $t('eventPropertyFilter.Page parameter'),
            filterType: FilterType.PageParameter,
            eventPropertyName: 'Page parameter',
            eventPropertySchema: STRING,
            eventPropertyElementType: STRING,
            eventPropertyFieldKey: PageParameterId,
            eventPropertyField: PageParameterId,
            eventPropertyKind: '',
            uniqueValues: [],
        },
    ];
}

/**
 * Create event property options (for the EP filter multiselect) based on the
 * given targets and known metadata.
 *
 * @param targets array of full target objects with the following partial structure
 * {
 *  kind: String,
 *  appId?: Number
 *  platform? String
 *  eventPropertyNameList? Array<String>
 *  eventPropertyConfigurations? Array<Object>
 * }
 */

export function setupEventProperties ({
    targets,
    eventProperties,
    historicalMetadata,
    accountMetadata,
    visitorMetadata,
    parentAccountMetadata,
    sessionProperties,
    setupForWebAnalytics = false,
    pagesWithPageParams,
    includeLocation,
    includeServer,
    showDeviceProperties = true,
}) {
    const deviceProps = getDeviceProps({ targets });
    const custom = getCustomEventProperties(eventProperties);
    const historical = getHistoricalMetadata(historicalMetadata);
    const account = getAccountMetadata(accountMetadata);
    const visitor = getVisitorMetadata(visitorMetadata);
    const parentAccount = getParentAccountMetadata(parentAccountMetadata);
    const sessionProps = getSessionProperties(sessionProperties);
    const locationProps = includeLocation ? getLocationProps() : [];
    const topLevelProps = includeServer ? getTopLevelProps() : [];

    return mapEventProperties({
        automaticEventProperties: deviceProps,
        customEventProperties: custom,
        historicalMetadata: historical,
        sessionProperties: sessionProps,
        grouped: true,
        showDeviceProperties,
        accountMetadata: account,
        visitorMetadata: visitor,
        parentAccountMetadata: parentAccount,
        setupForWebAnalytics,
        pagesWithPageParams,
        locationEventProperties: locationProps,
        topLevelEventProperties: topLevelProps,
    });
}

export function mapEventProperties ({
    automaticEventProperties = [],
    locationEventProperties = [],
    topLevelEventProperties = [],
    customEventProperties = [],
    historicalMetadata = [],
    sessionProperties = [],
    accountMetadata = [],
    visitorMetadata = [],
    parentAccountMetadata = [],
    grouped = false,
    setupForWebAnalytics = false,
    pagesWithPageParams = [],
    showDeviceProperties = true,
}) {
    const hasSessionProperties = sessionProperties.length > 0;
    const pageParam = pagesWithPageParams.length > 0 ? getPageParameterProps() : [];

    if (grouped) {
        const res = [];

        if (showDeviceProperties) {
            res.push({
                eventType: 'Device Data',
                eventPropertyName: uuid(),
                eventProps: automaticEventProperties,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('Device data'),
            });
        }

        if (pageParam.length) {
            res.push({
                eventType: 'Page Data',
                eventPropertyName: uuid(),
                eventProps: pageParam,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('Page data'),
            });
        }

        if (topLevelEventProperties.length) {
            res.push({
                eventType: 'Event Data',
                eventPropertyName: uuid(),
                eventProps: topLevelEventProperties,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('Event data'),
            });
        }

        if (locationEventProperties.length) {
            res.push({
                eventType: 'Location Data',
                eventPropertyName: uuid(),
                eventProps: locationEventProperties,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('Location data'),
            });
        }

        if (hasSessionProperties) {
            const sessionProps = {
                eventType: 'Session Properties',
                eventPropertyName: uuid(),
                eventProps: sessionProperties,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('Session properties'),
            };

            if (setupForWebAnalytics) {
                res.unshift(sessionProps);
            } else {
                res.push(sessionProps);
            }
        }

        if (customEventProperties.length) {
            res.push({
                eventType: 'Custom',
                eventPropertyName: uuid(),
                eventProps: customEventProperties,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('Custom event properties'),
            });
        }

        if (accountMetadata.length) {
            res.push({
                eventType: 'Account Metadata',
                eventPropertyName: uuid(),
                eventProps: accountMetadata,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('Account metadata'),
            });
        }

        if (visitorMetadata.length) {
            res.push({
                eventType: 'Visitor Metadata',
                eventPropertyName: uuid(),
                eventProps: visitorMetadata,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('Visitor metadata'),
            });
        }

        if (parentAccountMetadata.length) {
            res.push({
                eventType: 'Parent Account Metadata',
                eventPropertyName: uuid(),
                eventProps: parentAccountMetadata,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('Parent Account metadata'),
            });
        }

        if (historicalMetadata.length) {
            res.push({
                eventType: 'Historical Metadata',
                eventPropertyName: uuid(),
                eventProps: historicalMetadata,
                eventPropertyFieldKey: uuid(),
                groupLabel: $t('Historical metadata'),
            });
        }

        return res;
    }

    const eventProps = [
        ...customEventProperties,
        ...automaticEventProperties,
        ...historicalMetadata,
        ...locationEventProperties,
        ...topLevelEventProperties,
        ...pageParam,
    ];

    if (hasSessionProperties) {
        if (setupForWebAnalytics) {
            eventProps.unshift(...sessionProperties);
        } else {
            eventProps.push(...sessionProperties);
        }
    }

    return eventProps;
}

export function coerceEventPropertyType (option, schema) {
    try {
        // BE sends us stringified uniqueValues and FE needs to parse to remove escape characters.
        option = JSON.parse(option);
    } catch {
        // do nothing
    }
    const stringifiedOption = isObject(option) ? JSON.stringify(option) : String(option);
    switch (schema) {
        case STRING:
            if (isObject(option)) {
                return { coercedValue: stringifiedOption, schema: STRING, isValid: false };
            }

            return { coercedValue: String(option), schema: STRING, isValid: true };
        case INTEGER:
            // check for string 'true' or 'false' to evaluate to 1 or 0 respectively
            if (isString(option) && option.toLocaleLowerCase() === 'true') {
                return { coercedValue: 1, schema: INTEGER, isValid: true };
            }
            if (isString(option) && option.toLocaleLowerCase() === 'false') {
                return { coercedValue: 0, schema: INTEGER, isValid: true };
            }

            if (!isNumeric(option) || !Number.isInteger(Number(option))) {
                return { coercedValue: stringifiedOption, schema: INTEGER, isValid: false };
            }

            return { coercedValue: Number(option), schema: INTEGER, isValid: true };
        case FLOAT:
            // check for string 'true' or 'false' to evaluate to 1.0 or 0.0 respectively
            if (isString(option) && option.toLocaleLowerCase() === 'true') {
                return { coercedValue: 1.0, schema: FLOAT, isValid: true };
            }
            if (isString(option) && option.toLocaleLowerCase() === 'false') {
                return { coercedValue: 0.0, schema: FLOAT, isValid: true };
            }

            if (!isNumeric(option)) {
                return { coercedValue: stringifiedOption, schema: FLOAT, isValid: false };
            }

            return { coercedValue: Number(option), schema: FLOAT, isValid: true };
        case BOOLEAN:
            if (!isBoolean(option) && !isBooleanStringOrNumber(option)) {
                return { coercedValue: stringifiedOption, schema: BOOLEAN, isValid: false };
            }

            // explicitly check for boolean string 'false' and '0' since Boolean('false' or '0') === true.
            // But we want to return false for these cases
            if (isString(option) && (option.toLowerCase() === 'false' || option === '0')) {
                return { coercedValue: false, schema: BOOLEAN, isValid: true };
            }

            return { coercedValue: Boolean(option), schema: BOOLEAN, isValid: true };
        case DATE:
        case TIME:
            if (isObject(option)) {
                return { coercedValue: stringifiedOption, schema, isValid: false };
            }

            return { coercedValue: stringifiedOption, schema, isValid: true };
        case LIST:
            return { coercedValue: stringifiedOption, schema, isValid: false };
        default:
            return { coercedValue: stringifiedOption, schema: 'unknown', isValid: false };
    }
}

export function isBooleanStringOrNumber (option) {
    const booleanStringOptions = ['true', 'false', '1', '0'];
    const booleanNumberOptions = [1, 0];

    if (isNumber(option)) {
        return booleanNumberOptions.includes(option);
    }

    return isString(option) && booleanStringOptions.includes(toLower(option));
}

export function isNumeric (option) {
    return !isNaN(option) && !isNaN(parseFloat(option));
}

export function getEventPropertyLabelBySchemaType (schema) {
    const schemaLabelMap = {
        [STRING]: $t('eventPropertyFilter.Text'),
        [BOOLEAN]: $t('eventPropertyFilter.Boolean'),
        [INTEGER]: $t('eventPropertyFilter.Number (int)'),
        [FLOAT]: $t('eventPropertyFilter.Number (float)'),
        [DATE]: $t('eventPropertyFilter.Date'),
        [TIME]: $t('eventPropertyFilter.Time'),
        [LIST]: $t('eventPropertyFilter.List'),
    };

    return schemaLabelMap[schema];
}
